import { useEffect, useState, useRef } from 'react'
import styles from './StatusBar.module.css'

import BatteryIcon from './BatteryIcon'
import DropdownLogo from './DropdownLogo'
import VolumeControl from './VolumeControl'
import { useSound } from '../../hooks/useSound'
import { SOUNDS } from '../../constants/sounds'
import { useTranslationUpdate } from '../../hooks/useTranslationUpdate'
import { getCurrentLanguage } from '../../translations'
import MiniMusicPlayer from '../MusicPlayer/MiniMusicPlayer'
import { useBattery } from '../../contexts/BatteryContext'

export default function StatusBar() {
  useTranslationUpdate()
  const currentLang = getCurrentLanguage()
  const { battery } = useBattery()
  const [time, setTime] = useState(new Date())
  const [showDropdown, setShowDropdown] = useState(false)
  const [isClosing, setIsClosing] = useState(false)
  const logoRef = useRef<HTMLImageElement>(null)
  const playClick = useSound(SOUNDS.CLICK, { volume: 0.3 })

  useEffect(() => {
    const t = setInterval(() => setTime(new Date()), 5)
    return () => {
      clearInterval(t)
    }
  }, [])

  const hhmm = new Intl.DateTimeFormat(currentLang, { 
    hour: '2-digit', 
    minute: '2-digit',
    hour12: currentLang === 'en-US'
  })
  
  const dow = new Intl.DateTimeFormat(currentLang, { weekday: 'short' })
  const md = new Intl.DateTimeFormat(currentLang, { month: 'short', day: '2-digit' })

  const handleToggleDropdown = () => {
    playClick()
    if (showDropdown) {
      setIsClosing(true)
      setTimeout(() => {
        setShowDropdown(false)
        setIsClosing(false)
      }, 150)
    } else {
      setShowDropdown(true)
    }
  }

  const handleCloseDropdown = () => {
    setIsClosing(true)
    setTimeout(() => {
      setShowDropdown(false)
      setIsClosing(false)
    }, 150)
  }

  const getBatteryColor = () => {
    if (battery <= 10) return '#DC3545' 
    if (battery <= 20) return '#FFC107' 
    return '#000000' 
  }

  const batteryColor = getBatteryColor()

  return (
    <div className={styles.bar}>
      <div className={styles.left}>
        <img 
          ref={logoRef}
          src="/images/logo.png" 
          alt="Logo" 
          className={styles.logo} 
          onClick={handleToggleDropdown}
          style={{ cursor: 'pointer' }}
        />
        <span className={styles.time}>{hhmm.format(time)}</span>
        <span className={styles.date}>{`${dow.format(time)} ${md.format(time)}`}</span>
      </div>

      <div className={styles.center}>
        <MiniMusicPlayer />
      </div>

      <div className={styles.right}>
        <img src="/images/icons/signal.png" alt="Signal" className={styles.icon} />
        <img src="/images/icons/wifi.png" alt="Wifi" className={styles.icon} />
        <VolumeControl />
        <span className={styles.percent} style={{ color: batteryColor }}>{Math.round(battery)}%</span>
        <BatteryIcon level={battery} width={36} fillColor={batteryColor} />
      </div>
      
      {showDropdown && (
        <DropdownLogo 
          logoRef={logoRef}
          isClosing={isClosing}
          onClose={handleCloseDropdown}
          onLanguageChange={() => {}}
        />
      )}
    </div>
  )
}